import type { CatalogHostDto } from '../../data/types'
import { priceShapeFor, priceShapeLabel, shapePriceMicrosPerHour } from './catalogPricing'
import { hostLabel } from './hostLabels'

/**
 * The catalog's filter and sort, as one value.
 *
 * Everything here is pure, so the page, the toolbar, the panel and the table
 * header all read and write the same state, and a shared link restores it
 * exactly. The URL parameter names are the ones the catalog has always used:
 * old links keep working, and a new sort is a new VALUE of `sort`, never a new
 * parameter.
 */

export type SortKey = 'price' | 'freeCpu' | 'freeMemory' | 'region' | 'tier' | 'organization'
export type SortDirection = 'asc' | 'desc'

export interface CatalogQuery {
  search: string
  region: string
  tier: string
  minCpu: string
  minRam: string
  /** A case-insensitive substring of the GPU model. Stored as `minGpu` in the URL, which old links carry. */
  gpuModel: string
  track: string
  verified: boolean
  sort: SortKey
  direction: SortDirection
}

export const SORT_KEYS: readonly SortKey[] = ['price', 'freeCpu', 'freeMemory', 'region', 'tier', 'organization']

export const DEFAULT_QUERY: CatalogQuery = {
  search: '',
  region: '',
  tier: '',
  minCpu: '',
  minRam: '',
  gpuModel: '',
  track: '',
  verified: false,
  sort: 'region',
  direction: 'asc',
}

/** The fields that count as filters. The sort is shown, never counted. */
export type CatalogFilterKey = 'search' | 'region' | 'tier' | 'minCpu' | 'minRam' | 'gpuModel' | 'track' | 'verified'
export const CATALOG_FILTER_KEYS: readonly CatalogFilterKey[] = ['search', 'region', 'tier', 'minCpu', 'minRam', 'gpuModel', 'track', 'verified']

/** The numeric filters, which must parse before they are applied. */
export type NumericFilterKey = 'minCpu' | 'minRam' | 'track'

/**
 * A non-negative decimal such as `4`, `1.5` or `.5`, trimmed, or `''`.
 *
 * Deliberately narrower than `Number()`: `''`, `' '`, `'1e3'`, `'0x10'` and
 * `'-1'` all convert to a number, and a filter that silently accepted one of
 * them is how an entry used to empty the whole list.
 */
export function numericFilterValue(raw: string): string {
  const trimmed = raw.trim()
  return /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed) ? trimmed : ''
}

export function defaultDirection(key: SortKey): SortDirection {
  return key === 'freeCpu' || key === 'freeMemory' ? 'desc' : 'asc'
}

export function directionLabels(key: SortKey): { asc: string; desc: string } {
  if (key === 'price') return { asc: 'Low → high', desc: 'High → low' }
  if (key === 'freeCpu' || key === 'freeMemory') return { asc: 'Least first', desc: 'Most first' }
  return { asc: 'A → Z', desc: 'Z → A' }
}

export function parseCatalogQuery(params: URLSearchParams): CatalogQuery {
  const rawSort = params.get('sort')
  const sort = SORT_KEYS.includes(rawSort as SortKey) ? rawSort as SortKey : DEFAULT_QUERY.sort
  const rawDirection = params.get('direction')
  return {
    search: params.get('search') ?? '',
    region: params.get('region') ?? '',
    tier: params.get('tier') ?? '',
    minCpu: numericFilterValue(params.get('minCpu') ?? ''),
    minRam: numericFilterValue(params.get('minRam') ?? ''),
    gpuModel: params.get('minGpu') ?? '',
    track: numericFilterValue(params.get('track') ?? ''),
    verified: params.get('verified') === 'true',
    sort,
    // An omitted direction is the key's own default, because that is when the
    // URL omits it. Anything that is not `desc` or `asc` is read as absent.
    direction: rawDirection === 'desc' || rawDirection === 'asc' ? rawDirection : defaultDirection(sort),
  }
}

/** The query string, without the `?`. Defaults are omitted, as they always were. */
export function serializeCatalogQuery(query: CatalogQuery): string {
  const params = new URLSearchParams()
  if (query.search) params.set('search', query.search)
  if (query.region) params.set('region', query.region)
  if (query.tier) params.set('tier', query.tier)
  if (query.minCpu) params.set('minCpu', query.minCpu)
  if (query.minRam) params.set('minRam', query.minRam)
  if (query.gpuModel) params.set('minGpu', query.gpuModel)
  if (query.track) params.set('track', query.track)
  if (query.sort !== DEFAULT_QUERY.sort) params.set('sort', query.sort)
  if (query.direction !== defaultDirection(query.sort)) params.set('direction', query.direction)
  if (query.verified) params.set('verified', 'true')
  return params.toString()
}

export function isFilterActive(query: CatalogQuery, key: CatalogFilterKey): boolean {
  return key === 'verified' ? query.verified : query[key] !== ''
}

export function activeFilterCount(query: CatalogQuery): number {
  return CATALOG_FILTER_KEYS.filter((key) => isFilterActive(query, key)).length
}

export function clearFilter(query: CatalogQuery, key: CatalogFilterKey): CatalogQuery {
  return { ...query, [key]: DEFAULT_QUERY[key] }
}

/** Every filter off; the sort is kept. "Reset all" is `DEFAULT_QUERY`. */
export function clearFilters(query: CatalogQuery): CatalogQuery {
  return { ...DEFAULT_QUERY, sort: query.sort, direction: query.direction }
}

/** A new sort key starts at its own default direction. */
export function withSortKey(query: CatalogQuery, sort: SortKey): CatalogQuery {
  return { ...query, sort, direction: defaultDirection(sort) }
}

/** One chip per active filter, in the panel's order. */
export function filterChips(query: CatalogQuery): Array<{ key: CatalogFilterKey; text: string }> {
  const text: Record<CatalogFilterKey, () => string> = {
    search: () => `Search: ${query.search}`,
    region: () => `Region: ${query.region}`,
    tier: () => `Tier: ${query.tier}`,
    minCpu: () => `${query.minCpu}+ cores`,
    minRam: () => `${query.minRam}+ GB RAM`,
    gpuModel: () => `GPU: ${query.gpuModel}`,
    track: () => `Track record ≥ ${query.track}%`,
    verified: () => 'Verified only',
  }
  return CATALOG_FILTER_KEYS.filter((key) => isFilterActive(query, key)).map((key) => ({ key, text: text[key]() }))
}

/** The sort key's name, as the panel and the chip say it. */
export function sortKeyLabel(key: SortKey): string {
  return { price: 'Price', freeCpu: 'Free CPU', freeMemory: 'Free memory', region: 'Region', tier: 'Tier', organization: 'Host name' }[key]
}

/** "Price for 1 core · 2 GB · 10 GB, low → high" — the price sort always names its shape. */
export function sortChipText(query: CatalogQuery): string {
  const direction = directionLabels(query.sort)[query.direction]
  const key = query.sort === 'price' ? `Price for ${priceShapeLabel(priceShapeFor(query))}` : sortKeyLabel(query.sort)
  // "A → Z" keeps its capitals; "Low → high" reads as a clause after the comma.
  return `${key}, ${/^[AZ] →/.test(direction) ? direction : direction.charAt(0).toLowerCase() + direction.slice(1)}`
}

export function pluralHosts(count: number): string {
  return `${count} ${count === 1 ? 'host' : 'hosts'}`
}

function applyFilters(hosts: CatalogHostDto[], query: CatalogQuery): CatalogHostDto[] {
  const search = query.search.toLowerCase()
  const gpuModel = query.gpuModel.toLowerCase()
  const minimumCpu = Number(query.minCpu)
  const minimumRam = Number(query.minRam)
  const minimumTrack = Number(query.track)
  return hosts
    .filter((host) => !search || [host.owner, host.organization, host.region, host.tier, host.available?.gpu ?? ''].join(' ').toLowerCase().includes(search))
    .filter((host) => !query.region || host.region === query.region)
    .filter((host) => !query.tier || host.tier === query.tier)
    .filter((host) => !query.minCpu || (host.available?.cpuMilli ?? 0) >= minimumCpu * 1000)
    .filter((host) => !query.minRam || (host.available?.memoryBytes ?? 0) >= minimumRam * 1024 ** 3)
    .filter((host) => !gpuModel || (host.available?.gpu ?? host.inventory?.gpu ?? '').toLowerCase().includes(gpuModel))
    // An unrated host has no score to compare, so a floor excludes it rather
    // than treating "no evidence" as a passing grade.
    .filter((host) => !query.track || (host.track_record.state === 'rated' && (host.track_record.score_percent ?? -1) >= minimumTrack))
    .filter((host) => !query.verified || host.audited)
}

/** A stable last resort, so equal hosts never swap places between renders. */
function byLabel(left: CatalogHostDto, right: CatalogHostDto): number {
  return hostLabel(left).localeCompare(hostLabel(right)) || left.owner.localeCompare(right.owner)
}

function compareNumbers(left: bigint | number, right: bigint | number): number {
  return left < right ? -1 : left > right ? 1 : 0
}

/**
 * A sort value with a rank that direction cannot reorder.
 *
 * `rank` orders groups — known before unknown, and for capacity a fresh
 * reading before a stale one — and applies in both directions. Direction
 * flips only `value`, within a rank. That is what keeps an unpriced host last
 * whether the tenant asked for cheapest or dearest first, and what stops a
 * host last heard from yesterday topping "Most free CPU".
 */
type Ranked = { rank: number; value: bigint | number | string }

function rankedValue(host: CatalogHostDto, query: CatalogQuery, shape: ReturnType<typeof priceShapeFor>): Ranked {
  switch (query.sort) {
    case 'price': {
      const price = shapePriceMicrosPerHour(host.unit_prices, shape)
      return price === null ? { rank: 1, value: 0 } : { rank: 0, value: price }
    }
    case 'freeCpu':
    case 'freeMemory': {
      const value = query.sort === 'freeCpu' ? host.available?.cpuMilli : host.available?.memoryBytes
      if (value === null || value === undefined || !Number.isFinite(value)) return { rank: 2, value: 0 }
      return { rank: host.observation_state === 'fresh' ? 0 : 1, value }
    }
    case 'tier': return { rank: 0, value: host.tier }
    case 'organization': return { rank: 0, value: hostLabel(host) }
    case 'region': return { rank: 0, value: host.region }
  }
}

export function applyCatalogQuery(hosts: CatalogHostDto[], query: CatalogQuery): CatalogHostDto[] {
  const shape = priceShapeFor(query)
  const sign = query.direction === 'asc' ? 1 : -1
  return applyFilters(hosts, query)
    .map((host) => ({ host, sortValue: rankedValue(host, query, shape) }))
    .sort((left, right) => {
      if (left.sortValue.rank !== right.sortValue.rank) return left.sortValue.rank - right.sortValue.rank
      const a = left.sortValue.value
      const b = right.sortValue.value
      const result = typeof a === 'string' || typeof b === 'string' ? String(a).localeCompare(String(b)) : compareNumbers(a, b)
      return sign * result || byLabel(left.host, right.host)
    })
    .map(({ host }) => host)
}
